import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { AdviseTask } from '../types/advise';
import { fetchAdviseTasks as apiFetchAdviseTasks } from '../Api/Advise/Tasks';
import { addError } from './Error';

export interface UseAdviseTasksReturn {
  tasks: AdviseTask[];
  fetchTasks: (engagementId?: string, forceRefresh?: boolean) => Promise<void>;
  clear: () => void;
}

const defaultData: AdviseTask[] = [];
const subject = new BehaviorSubject<AdviseTask[]>(defaultData);
const loadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('advise-task', message);

export const getAdviseTaskSubject = (): BehaviorSubject<AdviseTask[]> => subject;
export const getAdviseTasks = (): AdviseTask[] => subject.value;
export const getAdviseTaskLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

export const useAdviseTasks = (): UseAdviseTasksReturn => {
  const [tasks, setTasks] = useState(subject.getValue());

  useEffect(() => {
    const sub = subject.subscribe(setTasks);
    return () => sub.unsubscribe();
  }, []);

  const fetchTasks = async (engagementId?: string, forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue().length > 0 && !forceRefresh) return;
    loadingSubject.next(true);

    const { data, error } = await apiFetchAdviseTasks(engagementId);
    if (data) {
      emitErrorState();
      subject.next(data);
      setTasks(data);
    }
    if (error) {
      subject.next(defaultData);
      emitErrorState(error.message);
    }
    loadingSubject.next(false);
  };

  const clear = () => {
    subject.next(defaultData);
    setTasks(defaultData);
  };

  return { tasks, fetchTasks, clear };
};
